import React, { useState, useEffect, useRef, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
    PaymentElement,
    Elements,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';
import { Box, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { createPaymentIntent, createTempCode } from '../../services/codeManagementApi';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PaymentFormContent = ({ amount, onSuccess, onError, codeValue, reason, codeId }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setProcessing(true);
        setError(null);

        try {
            if (!stripe || !elements) {
                throw new Error('Payment system is not ready');
            }

            const result = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: `${window.location.origin}/payment-complete`,
                },
                redirect: 'if_required'
            });

            if (result.error) {
                throw result.error;
            }

            console.log('Payment successful:', result);
            onSuccess({ paymentIntent: result.paymentIntent, codeId });
        } catch (error) {
            console.error('Payment error:', error);
            setError(error.message || 'Payment failed');
            onError(error);
        } finally {
            setProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Payment Amount: ${(amount / 100).toFixed(2)}
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <PaymentElement />
                </Box>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <Button 
                    type="submit"
                    variant="contained"
                    disabled={!stripe || processing}
                    fullWidth
                >
                    {processing ? 'Processing...' : 'Pay Now'}
                </Button>
            </Box>
        </form>
    );
};

const PaymentForm = ({ 
    amount, 
    codeValue, 
    reason,
    onSuccess, 
    onError,
    onCancel,
    codeFormData 
}, ref) => {
    const [clientSecret, setClientSecret] = useState(null);
    const [error, setError] = useState(null);
    const [tempCodeId, setTempCodeId] = useState(null);
    const requestInProgress = useRef(false);
    const mounted = useRef(false);
    const paymentSuccessful = useRef(false);

    const cleanup = useCallback(() => {
        if (tempCodeId && !paymentSuccessful.current) {
            console.log('Cleaning up temp code:', tempCodeId);
            onCancel?.(tempCodeId);
        }
    }, [tempCodeId, onCancel]);

    useImperativeHandle(ref, () => ({
        cleanup
    }), [cleanup]);

    useEffect(() => {
        console.log('PaymentForm mounting with:', { amount, codeValue, reason });
        mounted.current = true;

        const initialize = async () => {
            if (requestInProgress.current) return;
            try {
                requestInProgress.current = true;
                const tempCodeResponse = await createTempCode({
                    ...codeFormData,
                    code_value: codeValue
                });
                
                if (!mounted.current) return;
                setTempCodeId(tempCodeResponse.code_id);

                const response = await createPaymentIntent(
                    amount, 
                    codeValue, 
                    reason, 
                    tempCodeResponse.code_id
                );
                
                if (!mounted.current) return;
                setClientSecret(response.clientSecret);
            } catch (err) {
                console.error('Payment setup error:', err);
                if (!mounted.current) return;
                setError(err.message || 'Failed to initialize payment');
                onError(err);
            } finally {
                requestInProgress.current = false;
            }
        };

        if (amount && codeValue && reason && codeFormData) {
            initialize();
        }

        return () => {
            console.log('PaymentForm unmounting');
            mounted.current = false;
            cleanup();
        };
    }, []); // Empty deps array since we handle everything internally

    const options = useMemo(() => ({
        clientSecret,
        appearance: {
            theme: 'stripe',
            variables: {
                colorPrimary: '#1976d2',
            }
        },
        loader: 'auto',
        paymentMethodOrder: ['apple_pay', 'google_pay', 'card']
    }), [clientSecret]);

    const handlePaymentSuccess = useCallback(({ paymentIntent, codeId }) => {
        paymentSuccessful.current = true;
        onSuccess({ paymentIntent, codeId });
    }, [onSuccess]);

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (!clientSecret) {
        return (
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={20} />
                <Typography>Loading payment options...</Typography>
            </Box>
        );
    }

    return (
        <Elements 
            stripe={stripePromise} 
            options={options}
        >
            <PaymentFormContent 
                amount={amount}
                onSuccess={handlePaymentSuccess}
                onError={onError}
                codeValue={codeValue}
                reason={reason}
                codeId={tempCodeId}
            />
        </Elements>
    );
};

// Wrap with forwardRef to expose ref
export default forwardRef(PaymentForm); 